<template>
  <b-col v-if="visible" sm="12" md="12" xl="12" class="mb-1">
    <Transition name="fold" mode="out-in">
      <div class="compact-medical-form">
        <!-- Compact All-in-One Form Layout -->
        <div class="form-section">

          <!-- Row 1: Visit Information -->
          <b-row class="mb-2">
            <b-col sm="12" md="3" lg="2">
              <b-form-group class="mb-2">
                <validation-provider
                  #default="{ errors }"
                  :rules="jenisKunjunganRules"
                  name="Jenis Kunjungan"
                >
                  <label class="compact-label">Jenis Kunjungan <span v-if="isBpjs" class="text-danger">*</span></label>
                  <b-form-radio-group
                    v-model="kunjunganSakit"
                    :disabled="disabled"
                    :options="[{ value: true, text: 'Sakit' }, { value: false, text: 'Sehat' }]"
                    buttons
                    button-variant="outline-primary"
                    size="sm"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col sm="12" md="2" lg="1" v-if="isRajal">
              <b-form-group class="mb-2">
                <label class="compact-label invisible">.</label>
                <b-form-checkbox v-model="promotif" v-if="!disabled">
                  Promotif
                </b-form-checkbox>
              </b-form-group>
            </b-col>
            <b-col sm="12" :md="isRajal ? 4 : 6" :lg="isRajal ? 6 : 7">
              <b-form-group class="mb-2">
                <validation-provider
                #default="{ errors }"
                :rules="keluhanRules"
                name="Keluhan"
                >
                  <label class="compact-label">Keluhan Utama <span v-if="isBpjs" class="text-danger">*</span></label>
                  <b-input-group size="sm">
                    <b-form-input
                      v-model="keluhan"
                      placeholder="Isikan singkat keluhan pasien..."
                      :disabled="disabled"
                      />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col sm="12" :md="isRajal ? 3 : 3" :lg="isRajal ? 3 : 3">
              <b-form-group class="mb-2" v-if="!disabled">
                <label class="compact-label">Tanda Vital</label>
                <div class="d-flex flex-column compact-switches">
                  <div class="d-flex align-items-center mb-25">
                    <b-form-checkbox
                      v-model="nilaiNormal"
                      switch
                      @change="handleNilaiNormalChange"
                      class="compact-switch"
                    />
                    <div class="d-flex align-items-center ml-25">
                      <label class="switch-label">Semua Nilai normal</label>
                      <AiBadge text="AI" size="sm" class="ml-1" />
                    </div>
                  </div>
                  <div class="d-flex align-items-center">
                    <b-form-checkbox
                      v-model="tandaVital"
                      switch
                      @change="handleTandaVitalChange"
                      class="compact-switch"
                    />
                    <label class="switch-label ml-25">Manual input</label>
                  </div>
                </div>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Row 2: Anthropometry -->
          <b-row class="mb-2">
            <b-col sm="12" md="6" lg="6">
              <label class="compact-label mb-2 font-weight-medium">Antropometri <span v-if="isBpjs" class="text-danger">*</span></label>
              <div class="d-flex align-items-center">
                <b-form-group class="mb-2 flex-1 mr-1">
                  <validation-provider
                    #default="{ errors }"
                    :rules="tinggiBadanRules"
                    name="Tinggi Badan"
                  >
                    <b-input-group size="sm">
                      <b-form-input
                        v-model="tinggi_badan"
                        placeholder="165"
                        type="number"
                        :disabled="disabled"
                      />
                      <template #append>
                        <b-input-group-text>cm</b-input-group-text>
                      </template>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
                <span class="mx-1 font-weight-bold text-muted">/</span>
                <b-form-group class="mb-2 flex-1 mx-1">
                  <validation-provider
                    #default="{ errors }"
                    :rules="beratBadanRules"
                    name="Berat Badan"
                  >
                    <b-input-group size="sm">
                      <b-form-input
                        v-model="berat_badan"
                        placeholder="70"
                        type="number"
                        :disabled="disabled"
                      />
                      <template #append>
                        <b-input-group-text>kg</b-input-group-text>
                      </template>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
                <span class="mx-1 font-weight-bold text-muted">=</span>
                <b-form-group class="mb-2 flex-1 ml-1">
                  <b-input-group size="sm">
                    <b-form-input
                      v-model="imt"
                      placeholder="IMT"
                      type="number"
                      readonly
                      class="bg-light"
                    />
                    <template #append>
                      <b-input-group-text>kg/m²</b-input-group-text>
                    </template>
                  </b-input-group>
                </b-form-group>
              </div>
            </b-col>
            <b-col sm="6" md="3" lg="3">
              <b-form-group class="mb-2">
                <validation-provider
                  #default="{ errors }"
                  :rules="lingkarPerutRules"
                  name="Lingkar Perut"
                >
                  <label class="compact-label">Lingkar Perut <span v-if="isBpjs" class="text-danger">*</span></label>
                  <b-input-group size="sm">
                    <b-form-input
                      v-model="lingkar_perut"
                      placeholder="80"
                      type="number"
                      :disabled="disabled"
                    />
                    <template #append>
                      <b-input-group-text>cm</b-input-group-text>
                    </template>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col sm="6" md="3" lg="3">
              <b-form-group class="mb-2">
                <label class="compact-label">Suhu Badan <span v-if="isBpjs" class="text-danger">*</span></label>
                <b-input-group size="sm">
                  <b-form-input
                    v-model="suhu_badan_display"
                    placeholder="36,7"
                    type="text"
                    :disabled="disabled"
                    @input="handleSuhuInput"
                    @blur="formatSuhuDisplay"
                    :state="suhuValidationError ? false : null"
                  />
                  <template #append>
                    <b-input-group-text>°C</b-input-group-text>
                  </template>
                </b-input-group>
                <small v-if="suhuValidationError" class="text-danger">{{ suhuValidationError }}</small>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Row 3: Optional Vital Signs (Show only when switch is enabled) -->
          <Transition name="vital-signs-fade">
            <b-row v-if="tandaVital" class="mb-2 vital-signs-row">
              <b-col sm="12" md="4" lg="3">
                <label class="compact-label">Tekanan Darah</label>
                <div class="d-flex align-items-center">
                  <b-form-group class="mb-2 flex-1 mr-1">
                    <validation-provider
                      #default="{ errors }"
                      rules="numeric|min_value:40|max_value:250"
                      name="Sistole"
                    >
                      <b-input-group size="sm">
                        <b-form-input
                          v-model="sistole"
                          placeholder="120"
                          type="number"
                          :disabled="disabled"
                        />
                      </b-input-group>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                  <span class="mx-1 font-weight-bold text-muted">/</span>
                  <b-form-group class="mb-2 flex-1 ml-1">
                    <validation-provider
                      #default="{ errors }"
                      rules="numeric|min_value:30|max_value:180"
                      name="Diastole"
                    >
                      <b-input-group size="sm">
                        <b-form-input
                          v-model="diastole"
                          placeholder="80"
                          type="number"
                          :disabled="disabled"
                        />
                        <template #append>
                          <b-input-group-text>mmHg</b-input-group-text>
                        </template>
                      </b-input-group>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </div>
              </b-col>
              <b-col sm="6" md="4" lg="3">
                <b-form-group class="mb-2">
                  <validation-provider
                    #default="{ errors }"
                    rules="numeric|min_value:30|max_value:160"
                    name="Heart Rate"
                  >
                    <label class="compact-label">Nadi</label>
                    <b-input-group size="sm">
                      <b-form-input
                        v-model="heart_rate"
                        placeholder="80"
                        type="number"
                        :disabled="disabled"
                      />
                      <template #append>
                        <b-input-group-text>bpm</b-input-group-text>
                      </template>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col sm="6" md="4" lg="3">
                <b-form-group class="mb-2">
                  <validation-provider
                    #default="{ errors }"
                    rules="numeric|min_value:5|max_value:70"
                    name="Respiratory Rate"
                  >
                    <label class="compact-label">Respirasi</label>
                    <b-input-group size="sm">
                      <b-form-input
                        v-model="resp_rate"
                        placeholder="20"
                        type="number"
                        :disabled="disabled"
                      />
                      <template #append>
                        <b-input-group-text>/min</b-input-group-text>
                      </template>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
          </Transition>

        </div>
      </div>
    </Transition>
  </b-col>
</template>

<script>
export default {
  name: 'PanelRekamMedis',
  props: {
    visible: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    value: {
      type: Object,
      default: null
    },
    isRajal: {
      type: Boolean,
      default: false
    },
    isBpjs: {
      type: Boolean,
      default: false
    },
    patient: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      keluhan: '',
      tinggi_badan: null,
      berat_badan: null,
      lingkar_perut: null,
      suhu_badan: null,
      suhu_badan_display: '',
      suhuValidationError: '',
      imt: null,
      sistole: null,
      diastole: null,
      resp_rate: null,
      heart_rate: null,
      kunjunganSakit: null,
      tandaVital: false,
      promotif: false,
      nilaiNormal: false,
    }
  },
  computed: {
    // Conditional validation rules based on BPJS status
    jenisKunjunganRules() {
      return this.isBpjs ? 'required' : ''
    },
    keluhanRules() {
      return this.isBpjs ? 'required|min:5' : 'min:5'
    },
    tinggiBadanRules() {
      return this.isBpjs ? 'required|numeric|min_value:30|max_value:250' : 'numeric|min_value:30|max_value:250'
    },
    beratBadanRules() {
      return this.isBpjs ? 'required|numeric|min_value:2|max_value:300' : 'numeric|min_value:2|max_value:300'
    },
    lingkarPerutRules() {
      return this.isBpjs ? 'required|numeric|min_value:25|max_value:300' : 'numeric|min_value:25|max_value:300'
    },
    patientAge() {
      if (!this.patient || !this.patient.tanggal_lahir) return null
      return this.$moment().diff(this.patient.tanggal_lahir, 'years')
    },
    allData() {
      return {
        kunjungan_sakit: this.kunjunganSakit,
        keluhan: this.keluhan,
        tinggi_badan: this.tinggi_badan,
        berat_badan: this.berat_badan,
        lingkar_perut: this.lingkar_perut,
        suhu_badan: this.suhu_badan,
        imt: this.imt,
        sistole: this.sistole,
        diastole: this.diastole,
        resp_rate: this.resp_rate,
        heart_rate: this.heart_rate,
        promotif: this.promotif && this.isRajal,
      }
    },
  },
  watch: {
    allData: {
      deep: true,
      handler() {
        this.$emit('input', this.allData)
      }
    },
    tinggi_badan() {
      this.calcBMI()
    },
    berat_badan() {
      this.calcBMI()
    },
    value: {
      handler() {
        this.setData()
      },
      deep: true,
      immediate: true
    }
  },
  created() {
    this.setData()
  },
  methods: {
    // Generate random number within range
    randomInRange(min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min
    },
    // Get normal ranges based on patient age
    getNormalRanges() {
      const age = this.patientAge
      if (age === null) return null
      
      // Based on the table provided by user
      if (age < 1) {
        // Bayi (0-1 th)
        return {
          sistole: { min: 90, max: 110 }, // estimate since no specific range given
          diastole: { min: 50, max: 70 }, // estimate since no specific range given
          heart_rate: { min: 100, max: 160 },
          resp_rate: { min: 30, max: 60 },
          suhu_badan: { min: 35.5, max: 37.5 },
          tinggi_badan: { min: 50, max: 80 }, // Based on WHO growth charts
          berat_badan: { min: 3, max: 12 }, // Based on WHO growth charts
          lingkar_perut: null // Not specified for babies
        }
      } else if (age >= 1 && age <= 5) {
        // Balita (1-5 th)
        return {
          sistole: { min: 86, max: 112 },
          diastole: { min: 42, max: 72 },
          heart_rate: { min: 70, max: 120 },
          resp_rate: { min: 20, max: 30 },
          suhu_badan: { min: 36.4, max: 37.5 },
          tinggi_badan: { min: 75, max: 110 }, // Based on growth charts
          berat_badan: { min: 10, max: 20 }, // Based on growth charts
          lingkar_perut: null // Not specified for toddlers
        }
      } else if (age >= 6 && age <= 12) {
        // Anak (6-12 th)
        return {
          sistole: { min: 97, max: 115 },
          diastole: { min: 57, max: 76 },
          heart_rate: { min: 75, max: 118 },
          resp_rate: { min: 16, max: 30 },
          suhu_badan: { min: 36.4, max: 37.5 },
          tinggi_badan: { min: 110, max: 150 }, // Based on growth charts
          berat_badan: { min: 20, max: 45 }, // Based on growth charts
          lingkar_perut: null // Not specified for children
        }
      } else if (age >= 13 && age <= 17) {
        // Remaja (13-17 th)
        return {
          sistole: { min: 100, max: 120 },
          diastole: { min: 65, max: 80 },
          heart_rate: { min: 60, max: 100 },
          resp_rate: { min: 12, max: 20 },
          suhu_badan: { min: 36.5, max: 37.5 },
          tinggi_badan: { min: 145, max: 175 }, // Based on growth charts
          berat_badan: { min: 40, max: 70 }, // Based on growth charts
          lingkar_perut: null // Not specified for teens
        }
      } else if (age >= 18 && age <= 59) {
        // Dewasa (18-59 th)
        const isAdultMale = this.patient?.jenis_kelamin === 'L' || this.patient?.jenis_kelamin === 1
        return {
          sistole: { min: 110, max: 130 },
          diastole: { min: 70, max: 85 },
          heart_rate: { min: 60, max: 100 },
          resp_rate: { min: 12, max: 20 },
          suhu_badan: { min: 36.0, max: 37.5 },
          tinggi_badan: { min: isAdultMale ? 160 : 150, max: isAdultMale ? 180 : 170 },
          berat_badan: { min: isAdultMale ? 55 : 45, max: isAdultMale ? 80 : 70 },
          lingkar_perut: { min: isAdultMale ? 65 : 60, max: isAdultMale ? 90 : 80 }
        }
      } else {
        // Lansia (≥60 th)
        const isElderlyMale = this.patient?.jenis_kelamin === 'L' || this.patient?.jenis_kelamin === 1
        return {
          sistole: { min: 120, max: 140 },
          diastole: { min: 70, max: 90 },
          heart_rate: { min: 60, max: 100 },
          resp_rate: { min: 12, max: 18 },
          suhu_badan: { min: 36.0, max: 37.5 },
          tinggi_badan: { min: isElderlyMale ? 155 : 145, max: isElderlyMale ? 175 : 165 },
          berat_badan: { min: isElderlyMale ? 50 : 40, max: isElderlyMale ? 75 : 65 },
          lingkar_perut: { min: isElderlyMale ? 70 : 65, max: isElderlyMale ? 90 : 80 }
        }
      }
    },
    // Handle manual input switch change
    handleTandaVitalChange(checked) {
      if (!checked) {
        // If disabling manual input, also disable normal values and clear vital signs
        this.nilaiNormal = false
        this.clearVitalSigns()
      }
    },
    // Handle normal values switch change
    handleNilaiNormalChange(checked) {
      if (checked) {
        // Auto-enable manual input when enabling normal values
        this.tandaVital = true
        this.setNormalValues()
      }
      // If unchecking, just leave the fields as they are for manual editing
    },
    // Check if existing anthropometric values are within reasonable ranges
    isValueReasonable(value, range) {
      if (!value || !range) return false
      // Allow 20% tolerance beyond the normal range to account for individual variations
      const tolerance = 0.2
      const extendedMin = range.min * (1 - tolerance)
      const extendedMax = range.max * (1 + tolerance)
      return value >= extendedMin && value <= extendedMax
    },
    // Set normal values based on patient age
    setNormalValues() {
      const ranges = this.getNormalRanges()
      if (!ranges) {
        this.$dekatoast.warning('Data pasien tidak lengkap. Tidak dapat menentukan nilai normal.')
        this.nilaiNormal = false
        return
      }
      
      let preservedCount = 0
      let overriddenFields = []
      
      // Smart anthropometry handling - preserve reasonable existing values
      if (ranges.tinggi_badan) {
        if (this.tinggi_badan && this.isValueReasonable(this.tinggi_badan, ranges.tinggi_badan)) {
          // Keep existing height if it's reasonable
          preservedCount++
        } else {
          // Override with normal value if missing or unreasonable
          this.tinggi_badan = this.randomInRange(ranges.tinggi_badan.min, ranges.tinggi_badan.max)
          overriddenFields.push('tinggi badan')
        }
      }
      
      if (ranges.berat_badan) {
        if (this.berat_badan && this.isValueReasonable(this.berat_badan, ranges.berat_badan)) {
          // Keep existing weight if it's reasonable
          preservedCount++
        } else {
          // Override with normal value if missing or unreasonable
          this.berat_badan = this.randomInRange(ranges.berat_badan.min, ranges.berat_badan.max)
          overriddenFields.push('berat badan')
        }
      }
      
      if (ranges.lingkar_perut) {
        if (this.lingkar_perut && this.isValueReasonable(this.lingkar_perut, ranges.lingkar_perut)) {
          // Keep existing waist circumference if reasonable
          preservedCount++
        } else {
          // Override with normal value if missing or unreasonable
          this.lingkar_perut = this.randomInRange(ranges.lingkar_perut.min, ranges.lingkar_perut.max)
          overriddenFields.push('lingkar perut')
        }
      }
      
      // Always set temperature to normal values (this changes frequently)
      if (ranges.suhu_badan) {
        const tempMin = ranges.suhu_badan.min * 10
        const tempMax = ranges.suhu_badan.max * 10
        this.suhu_badan = this.randomInRange(tempMin, tempMax) / 10
        this.suhu_badan_display = this.suhu_badan.toString().replace('.', ',')
      }
      
      // Always set vital signs to normal values (these change with each visit)
      if (ranges.sistole) {
        this.sistole = this.randomInRange(ranges.sistole.min, ranges.sistole.max)
      }
      if (ranges.diastole) {
        this.diastole = this.randomInRange(ranges.diastole.min, ranges.diastole.max)
      }
      if (ranges.heart_rate) {
        this.heart_rate = this.randomInRange(ranges.heart_rate.min, ranges.heart_rate.max)
      }
      if (ranges.resp_rate) {
        this.resp_rate = this.randomInRange(ranges.resp_rate.min, ranges.resp_rate.max)
      }
      
      // Calculate BMI if height and weight are set
      this.calcBMI()
      
      // Provide feedback about what was preserved vs overridden
      if (preservedCount > 0 && overriddenFields.length > 0) {
        this.$dekatoast.success(`Nilai normal diisi. Data sebelumnya dipertahankan untuk nilai yang masih wajar. Diperbarui: ${overriddenFields.join(', ')}`)
      } else if (preservedCount > 0) {
        this.$dekatoast.success('Nilai normal diisi. Data antropometri sebelumnya dipertahankan karena masih dalam rentang wajar.')
      } else {
        this.$dekatoast.success('Nilai normal berhasil diisi berdasarkan usia pasien')
      }
    },
    // Clear vital signs values
    clearVitalSigns() {
      this.sistole = null
      this.diastole = null
      this.heart_rate = null
      this.resp_rate = null
    },
    setData() {
      this.kunjunganSakit = this.value?.kunjungan_sakit
      this.keluhan = this.value?.keluhan
      this.tinggi_badan = this.value?.tinggi_badan
      this.berat_badan = this.value?.berat_badan
      this.lingkar_perut = this.value?.lingkar_perut
      this.suhu_badan = this.value?.suhu_badan
      this.suhu_badan_display = this.value?.suhu_badan ? this.value.suhu_badan.toString().replace('.', ',') : ''
      this.imt = this.value?.imt
      this.sistole = this.value?.sistole
      this.diastole = this.value?.diastole
      this.heart_rate = this.value?.heart_rate
      this.resp_rate = this.value?.resp_rate
      this.promotif = this.value?.promotif
      this.tandaVital = this.value?.sistole ? true : false;
    },
    unsetData() {
      this.keluhan = ''
      this.tinggi_badan = null
      this.berat_badan = null
      this.lingkar_perut = null
      this.suhu_badan = null
      this.suhu_badan_display = ''
      this.suhuValidationError = ''
      this.imt = null
      this.sistole = null
      this.diastole = null
      this.heart_rate = null
      this.resp_rate = null
      this.kunjunganSakit = null
      this.tandaVital = false
      this.nilaiNormal = false
    },
    calcBMI() {
      if (!this.berat_badan || !this.tinggi_badan) {
        this.imt = null
        return
      }
      this.imt = ((this.berat_badan / (this.tinggi_badan * this.tinggi_badan)) * 10000).toFixed(1)
    },
    bmiStr() {
      if (!this.imt) return ''
      if (this.imt < 17) return 'Sangat Kurus'
      if (this.imt < 18.4) return 'Kurus'
      if (this.imt < 25.0) return 'Normal'
      if (this.imt < 27) return 'Gemuk'
      return 'Obesitas'
    },
    handleSuhuInput(value) {
      // Clear previous validation error
      this.suhuValidationError = ''
      
      // Handle empty input
      if (value === '' || value === null || value === undefined) {
        this.suhu_badan = null
        this.suhuValidationError = this.isBpjs ? 'Suhu badan wajib diisi' : ''
        return
      }
      
      // Allow input with comma or dot as decimal separator
      const cleanValue = value.replace(',', '.')
      const numValue = parseFloat(cleanValue)
      
      // Validate numeric input
      if (isNaN(numValue)) {
        this.suhuValidationError = 'Suhu badan harus berupa angka'
        this.suhu_badan = null
        return
      }
      
      // Validate range
      if (numValue < 35 || numValue > 42) {
        this.suhuValidationError = 'Suhu badan harus antara 35-42°C'
        this.suhu_badan = null
        return
      }
      
      // Update the internal numeric value if valid
      this.suhu_badan = numValue
    },
    formatSuhuDisplay() {
      // Format display value with comma as decimal separator
      if (this.suhu_badan !== null && this.suhu_badan !== undefined) {
        this.suhu_badan_display = this.suhu_badan.toString().replace('.', ',')
      } else {
        this.suhu_badan_display = ''
      }
    },
  }
}
</script>

<style lang="scss" scoped>
// Compact medical form styling
.compact-medical-form {
  background: #ffffff;
  border-radius: 0.25rem;

  .form-section {
    padding: 0.75rem;
    background: #ffffff;
  }
}

// Compact label styling
.compact-label {
  font-weight: 600;
  color: #495057;
  font-size: 0.75rem;
  margin-bottom: 0.25rem !important;
  display: block;
}

// Compact form groups
.form-group {
  margin-bottom: 0.5rem !important;
}

// Small input styling
.form-control {
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem;
  height: auto;

  &:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.1rem rgba(0, 123, 255, 0.25);
  }

  &.bg-light {
    background-color: #f8f9fa !important;
    border-color: #ced4da;
  }

  &::placeholder {
    color: #6c757d;
    font-size: 0.8rem;
  }
}

// Input group styling
.input-group-sm {
  .form-control {
    height: 32px;
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
  }

  .input-group-text {
    background-color: #e9ecef;
    border: 1px solid #ced4da;
    color: #495057;
    font-weight: 600;
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;
    height: 32px;
    display: flex;
    align-items: center;
  }
}

// Radio button styling - more compact
.btn-group-toggle {
  .btn-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.775rem;
  }

  .btn-outline-primary {
    border: 1px solid #007bff;
    color: #007bff;

    &:hover, &:focus {
      background-color: #007bff;
      border-color: #007bff;
      color: white;
    }

    &.active {
      background-color: #007bff !important;
      border-color: #007bff !important;
      color: white !important;
    }
  }
}

// Blood pressure layout
.flex-1 {
  flex: 1;
}

// Vital signs row styling
.vital-signs-row {
  border-top: 1px solid #e9ecef;
  padding-top: 0.5rem;
  background: #f8f9fa;
  margin-left: -0.75rem;
  margin-right: -0.75rem;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  border-radius: 0 0 0.25rem 0.25rem;
}

// Compact checkbox
.compact-checkbox {
  .custom-control-label {
    font-size: 0.75rem;
    font-weight: 500;
  }
}

// Compact switches
.compact-switches {
  .compact-switch {
    .custom-control-label {
      &::before {
        width: 2rem;
        height: 1.2rem;
        border-radius: 0.6rem;
        background-color: #ced4da;
        border: none;
      }
      
      &::after {
        width: 1rem;
        height: 1rem;
        border-radius: 50%;
        background-color: white;
        top: 0.1rem;
        left: 0.1rem;
        transition: transform 0.15s ease-in-out;
      }
    }
    
    .custom-control-input:checked ~ .custom-control-label {
      &::before {
        background-color: #007bff;
        border-color: #007bff;
      }
      
      &::after {
        transform: translateX(0.8rem);
      }
    }
    
    .custom-control-input:focus ~ .custom-control-label::before {
      box-shadow: 0 0 0 0.1rem rgba(0, 123, 255, 0.25);
    }
  }
  
  .switch-label {
    font-size: 0.75rem;
    font-weight: 500;
    color: #495057;
    cursor: pointer;
    margin-bottom: 0;
    user-select: none;
  }
  
  .mb-25 {
    margin-bottom: 0.25rem !important;
  }
  
}

// Row spacing
.mb-2 {
  margin-bottom: 0.5rem !important;
}

// Form validation styling
small.text-danger {
  font-size: 0.675rem;
  margin-top: 0.125rem;
  display: block;
}

// Invisible label for alignment
.invisible {
  visibility: hidden !important;
  font-size: 0.75rem;
}

// Responsive adjustments
@media (max-width: 992px) {
  .compact-medical-form .form-section {
    padding: 0.5rem;
  }
}

@media (max-width: 768px) {
  .compact-medical-form {
    .form-section {
      padding: 0.375rem;
    }
  }

  // Stack blood pressure fields vertically on tablet
  .vital-signs-row {
    .d-flex.align-items-center {
      flex-direction: column;
      align-items: stretch !important;

      .flex-1 {
        width: 100%;
        margin: 0 !important;
      }

      .mx-1 {
        margin: 0.25rem 0 !important;
        text-align: center;
      }
    }
  }
}

@media (max-width: 576px) {
  .compact-medical-form {
    .form-section {
      padding: 0.25rem;
    }
  }

  .compact-label {
    font-size: 0.7rem;
  }

  .form-control {
    font-size: 0.8rem;
  }

  .input-group-sm {
    .form-control {
      height: 28px;
      font-size: 0.8rem;
    }

    .input-group-text {
      font-size: 0.65rem;
      height: 28px;
    }
  }

  // More compact spacing on mobile
  .mb-2 {
    margin-bottom: 0.375rem !important;
  }

  .form-group {
    margin-bottom: 0.375rem !important;
  }
  
}

// Main fold transition
.fold-enter-active,
.fold-leave-active {
  transition: all 0.25s;
}
.fold-enter,
.fold-leave-to {
  transform: scaleY(0) translateY(-100%);
  opacity: 0;
}

// Remove extra margins
.mr-1 {
  margin-right: 0.25rem !important;
}

.ml-1 {
  margin-left: 0.25rem !important;
}

.mx-1 {
  margin-left: 0.25rem !important;
  margin-right: 0.25rem !important;
}

// Text color consistency
.text-muted {
  color: #6c757d !important;
}

.text-danger {
  color: #dc3545 !important;
}

// Ensure consistent small text
small {
  font-size: 0.675rem;
}
</style>